// lib/theme.ts
//
// Small helpers for the Ramp/Linear mode system. The mode lives in
// user_profiles.preferences.mode (JSONB) and is applied to the root <html>
// element via `data-mode={mode}` by the app layout server component.

export type Mode = "ramp" | "linear";

// Vault-v3 is light-first (the authed ops surfaces are designed light; a
// proper dark mode was never built). Default to `ramp` (light) so the ops
// canvas — painted by <body> from the root data-mode, OUTSIDE the .app-shell
// ops-v3 skin scope — matches the light vault-v3 cards instead of falling back
// to the un-designed dark `linear` palette (white cards on a black body).
// HQ/CU/Chef self-drive their own theme scopes and are unaffected by this value.
export const DEFAULT_MODE: Mode = "ramp";
export const MODE_COOKIE_NAME = "fb_mode";
export const MODE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const VALID_MODES = new Set<Mode>(["ramp", "linear"]);

/** Extract the persisted mode from a user_profiles.preferences row. */
export function readMode(preferences: unknown): Mode {
  if (!preferences || typeof preferences !== "object") return DEFAULT_MODE;
  const raw = (preferences as Record<string, unknown>).mode;
  return typeof raw === "string" && VALID_MODES.has(raw as Mode)
    ? (raw as Mode)
    : DEFAULT_MODE;
}

/** Normalise an arbitrary input to a valid Mode, falling back to default. */
export function coerceMode(mode: unknown): Mode {
  return typeof mode === "string" && VALID_MODES.has(mode as Mode)
    ? (mode as Mode)
    : DEFAULT_MODE;
}

/** Read the SSR paint mode from the lightweight mode cookie. */
export function readModeCookie(value: unknown): Mode {
  return coerceMode(value);
}
